// avatar.js — avatar rendering + image processing utilities.
//
// Four pure helpers, all DOM/crypto + no state:
//
//   sha256Hex(input)
//     SHA-256 hex digest via crypto.subtle. Used as the Gravatar
//     identifier (Gravatar accepts SHA-256 as of 2023; lets us
//     skip a 6 KB MD5 polyfill).
//
//   gravatarURL(email)
//     Returns the Gravatar URL for an email (with identicon
//     fallback). Async because sha256Hex is.
//
//   inlineInitialAvatar(name)
//     Returns a `data:image/svg+xml;…` URL with a colored circle
//     + uppercase first letter of `name`. Used as the empty-state
//     avatar when no email is available.
//
//   resizeImageToDataURL(file, size)
//     Cover-fits + center-crops `file` to a `size`×`size` canvas
//     and returns a JPEG data URL at quality 0.85. Used by the
//     avatar upload flow.
//
// Hoisted from bootstrap.js on 2026-05-13.
import { escapeHTML } from "./util.js";

export async function sha256Hex(input) {
  const buf = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(input),
  );
  const arr = Array.from(new Uint8Array(buf));
  return arr.map((b) => b.toString(16).padStart(2, "0")).join("");
}

export async function gravatarURL(email) {
  if (!email) return "";
  const hash = await sha256Hex(email.trim().toLowerCase());
  return "https://www.gravatar.com/avatar/" + hash + "?s=56&d=identicon";
}

// inlineInitialAvatar: solid-fill circle + white letter. Earlier
// versions tried a translucent circle (rgba alpha 0.18) which
// rendered as "almost black" on the dark theme because the
// translucent blue composited against the dark page background.
// Solid fill + white text reads correctly against both themes.
export function inlineInitialAvatar(name) {
  const letter = (
    name && name.trim() ? name.trim().charAt(0) : "?"
  ).toUpperCase();
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">
      <circle cx="28" cy="28" r="28" fill="#3b82f6"/>
      <text x="50%" y="50%" dy="0.35em" text-anchor="middle"
            fill="#ffffff" font-family="system-ui, -apple-system, sans-serif"
            font-size="28" font-weight="700">${escapeHTML(letter)}</text>
    </svg>`;
  return "data:image/svg+xml;utf8," + encodeURIComponent(svg);
}

// resizeImageToDataURL: cover-fit / center-crop so squares look
// right, then encode as JPEG @ 0.85. The worker caps the data URL
// at 100KB; 128×128 JPEG @ 0.85 is typically 6-12KB for photos so
// we have plenty of headroom.
export function resizeImageToDataURL(file, size) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read failed"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("invalid image"));
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        const scale = Math.max(size / img.width, size / img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
